import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Timer,
  Add,
  Close,
  ExpandMore,
  ExpandLess,
  Save,
  Edit,
  Refresh,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import CaseAutocomplete from '../Common/CaseAutocomplete';
import ClientAutocomplete from '../Common/ClientAutocomplete';
import { getAuthToken } from '../../lib/authToken';

interface TimeEntry {
  id: string;
  startTime: string;
  endTime?: string;
  duration: number; // in seconds
  description: string;
  caseId?: string;
  clientId?: string;
  taskType: string;
  billable: boolean;
  rate?: number;
  amount?: number;
  status: 'active' | 'paused' | 'completed';
  createdAt: string;
  updatedAt: string;
}

interface TimerWidgetProps {
  onTimeEntrySave?: (entry: TimeEntry) => void;
}

const TIMER_STORAGE_KEY = 'argislegal_global_timer';

const loadTimerState = () => {
  try {
    const raw = sessionStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const TimerWidget: React.FC<TimerWidgetProps> = ({ onTimeEntrySave }) => {
  const queryClient = useQueryClient();
  const saved = loadTimerState();
  const [isExpanded, setIsExpanded] = useState(!!saved?.isRunning || !!saved?.isPaused);
  const [isMinimized, setIsMinimized] = useState(!(saved?.isRunning || saved?.isPaused));
  const [isRunning, setIsRunning] = useState(!!saved?.isRunning);
  const [isPaused, setIsPaused] = useState(!!saved?.isPaused);
  const [elapsedTime, setElapsedTime] = useState(() => {
    if (!saved) return 0;
    if (saved.isRunning && !saved.isPaused && saved.startedAt) {
      const drift = Math.floor((Date.now() - saved.startedAt) / 1000);
      return Math.max(0, (saved.elapsedTime || 0) + drift);
    }
    return saved.elapsedTime || 0;
  });
  const [description, setDescription] = useState(saved?.description || '');
  const [selectedCase, setSelectedCase] = useState(saved?.selectedCase || '');
  const [selectedClient, setSelectedClient] = useState(saved?.selectedClient || '');
  const [taskType, setTaskType] = useState(saved?.taskType || 'legal_work');
  const [billable, setBillable] = useState(saved?.billable !== false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<Partial<TimeEntry> | null>(null);
  const [startNewAfterSave, setStartNewAfterSave] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<Date | null>(saved?.startTime ? new Date(saved.startTime) : null);

  // Save time entry mutation
  const saveTimeEntryMutation = useMutation({
    mutationFn: async (entry: Partial<TimeEntry>) => {
      console.log('Mutation called with entry:', entry);
      const token = getAuthToken();
      console.log('Token:', token ? 'Present' : 'Missing');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch('/api/time-entries', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });
      
      console.log('Response status:', response.status);
      if (!response.ok) {
        const message = await getApiErrorMessage(response, 'Failed to save time entry.');
        console.error('Time entry save error:', message);
        throw new Error(message);
      }
      
      const result = await response.json();
      console.log('Response result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('Mutation success:', data);
      toast.success('Time entry saved successfully');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      onTimeEntrySave?.(data.data);
      
      // Close the dialog first
      setShowSaveDialog(false);
      
      // Reset timer after dialog is closed
      resetTimer();
      
      // If user wanted to start a new timer after saving
      if (startNewAfterSave) {
        setStartNewAfterSave(false);
        setTimeout(() => {
          startNewTimer();
        }, 100);
      }
    },
    onError: (error: Error) => {
      console.error('Mutation error:', error);
      toast.error(toUserErrorMessage(error, 'Failed to save time entry.'));
    },
  });

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = window.setInterval(() => {
        setElapsedTime((prev: number) => prev + 1);
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused]);

  // Persist stopwatch across navigations / refresh within the session
  useEffect(() => {
    const payload = {
      isRunning,
      isPaused,
      elapsedTime,
      description,
      selectedCase,
      selectedClient,
      taskType,
      billable,
      startTime: startTimeRef.current?.toISOString() || null,
      startedAt: isRunning && !isPaused ? Date.now() - elapsedTime * 1000 : null,
    };
    if (!isRunning && !isPaused && elapsedTime === 0) {
      sessionStorage.removeItem(TIMER_STORAGE_KEY);
    } else {
      sessionStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(payload));
    }
  }, [isRunning, isPaused, elapsedTime, description, selectedCase, selectedClient, taskType, billable]);

  const startTimer = () => {
    setIsRunning(true);
    setIsPaused(false);
    setIsMinimized(false);
    setIsExpanded(true);
    startTimeRef.current = new Date();
    setCurrentEntry({
      startTime: new Date().toISOString(),
      status: 'active',
      billable,
      taskType,
    });
  };

  const pauseTimer = () => {
    setIsPaused(true);
    setIsRunning(false);
    if (currentEntry) {
      setCurrentEntry({
        ...currentEntry,
        status: 'paused',
      });
    }
  };

  const resumeTimer = () => {
    setIsRunning(true);
    setIsPaused(false);
    if (currentEntry) {
      setCurrentEntry({
        ...currentEntry,
        status: 'active',
      });
    }
  };

  const stopTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setShowSaveDialog(true);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setElapsedTime(0);
    setDescription('');
    setSelectedCase('');
    setSelectedClient('');
    setTaskType('legal_work');
    setBillable(true);
    setCurrentEntry(null);
    setShowSaveDialog(false);
    setStartNewAfterSave(false);
    setIsMinimized(true);
    setIsExpanded(false);
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
  };

  const startNewTimer = () => {
    // If there's an active timer, ask user what to do
    if (isRunning || isPaused) {
      if (window.confirm('You have an active timer. Do you want to save it and start a new timer?')) {
        setStartNewAfterSave(true);
        setShowSaveDialog(true);
        return;
      } else {
        // User chose not to save, just start fresh
        resetTimer();
        setIsRunning(true);
        setIsPaused(false);
        startTimeRef.current = new Date();
        setCurrentEntry({
          startTime: new Date().toISOString(),
          status: 'active',
          billable,
          taskType,
        });
        return;
      }
    }
    
    // Reset everything for a fresh start
    resetTimer();
    setIsRunning(true);
    setIsPaused(false);
    startTimeRef.current = new Date();
    setCurrentEntry({
      startTime: new Date().toISOString(),
      status: 'active',
      billable,
      taskType,
    });
  };

  const handleSaveEntry = async () => {
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const entry: Partial<TimeEntry> = {
      ...currentEntry,
      endTime: new Date().toISOString(),
      duration: elapsedTime,
      description: description.trim(),
      caseId: selectedCase || undefined,
      clientId: selectedClient || undefined,
      taskType,
      billable,
      status: 'completed',
    };

    console.log('Saving entry:', entry);
    try {
      await saveTimeEntryMutation.mutateAsync(entry);
    } catch (error) {
      console.error('Error in handleSaveEntry:', error);
      toast.error('Failed to save time entry');
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {isMinimized && !isRunning && !isPaused ? (
        <Tooltip title="Open stopwatch">
          <Fab
            color="primary"
            aria-label="Open stopwatch"
            onClick={() => {
              setIsMinimized(false);
              setIsExpanded(true);
            }}
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              zIndex: 1300,
              pb: 'env(safe-area-inset-bottom)',
            }}
          >
            <Timer />
          </Fab>
        </Tooltip>
      ) : (
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
          zIndex: 1300,
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Card
          sx={{
            minWidth: isExpanded ? { xs: '100%', sm: 350 } : 200,
            maxWidth: isExpanded ? 400 : 220,
            width: isExpanded ? { xs: 'min(100%, 400px)', sm: 'auto' } : 'auto',
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: isExpanded ? 2 : 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isExpanded ? 1 : 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Timer sx={{ mr: 1, color: 'primary.main', fontSize: isExpanded ? 'inherit' : '1.2rem' }} />
                <Typography variant={isExpanded ? "h6" : "body1"} sx={{ fontWeight: 'bold' }}>
                  Timer
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => setIsExpanded(!isExpanded)}
                  aria-label={isExpanded ? 'Collapse timer' : 'Expand timer'}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                {!isRunning && !isPaused && (
                  <IconButton
                    size="small"
                    onClick={() => setIsMinimized(true)}
                    aria-label="Minimize timer"
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Typography variant={isExpanded ? "h4" : "h5"} sx={{ fontFamily: 'monospace', textAlign: 'center', mb: isExpanded ? 2 : 1 }}>
              {formatTime(elapsedTime)}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: isExpanded ? 1 : 0.5, mb: isExpanded ? 2 : 1 }}>
              {!isRunning && !isPaused && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={startTimer}
                    color="success"
                    size={isExpanded ? "medium" : "small"}
                  >
                    {isExpanded ? "Start" : ""}
                  </Button>
                  <Tooltip title="Start a fresh timer for a new case/project">
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={startNewTimer}
                      color="primary"
                      size={isExpanded ? "medium" : "small"}
                    >
                      {isExpanded ? "New Timer" : ""}
                    </Button>
                  </Tooltip>
                  {elapsedTime > 0 && (
                    <Button
                      variant="text"
                      startIcon={<Refresh />}
                      onClick={resetTimer}
                      color="warning"
                      size="small"
                    >
                      {isExpanded ? "Reset" : ""}
                    </Button>
                  )}
                </>
              )}
              
              {isRunning && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Pause />}
                    onClick={pauseTimer}
                    size={isExpanded ? "medium" : "small"}
                  >
                    {isExpanded ? "Pause" : ""}
                  </Button>
                  <Tooltip title="Start a fresh timer for a new case/project">
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={startNewTimer}
                      color="primary"
                      size="small"
                    >
                      {isExpanded ? "New" : ""}
                    </Button>
                  </Tooltip>
                </>
              )}
              
              {isPaused && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={resumeTimer}
                    color="success"
                    size={isExpanded ? "medium" : "small"}
                  >
                    {isExpanded ? "Resume" : ""}
                  </Button>
                  <Tooltip title="Start a fresh timer for a new case/project">
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={startNewTimer}
                      color="primary"
                      size="small"
                    >
                      {isExpanded ? "New" : ""}
                    </Button>
                  </Tooltip>
                </>
              )}
              
              {(isRunning || isPaused) && (
                <Button
                  variant="contained"
                  startIcon={<Stop />}
                  onClick={stopTimer}
                  color="error"
                  size={isExpanded ? "medium" : "small"}
                >
                  {isExpanded ? "Stop" : ""}
                </Button>
              )}
            </Box>

            <Collapse in={isExpanded}>
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What are you working on?"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <CaseAutocomplete
                    label="Case"
                    size="small"
                    value={selectedCase || null}
                    onChange={(c) => {
                      setSelectedCase(c?.id || '');
                      const clientId = c?.clientId || c?.client?.id;
                      if (clientId) setSelectedClient(clientId);
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <ClientAutocomplete
                    label="Client"
                    size="small"
                    value={selectedClient || null}
                    onChange={(c) => setSelectedClient(c?.id || '')}
                  />
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    label="Task Type"
                  >
                    <MenuItem value="legal_work">Legal Work</MenuItem>
                    <MenuItem value="research">Research</MenuItem>
                    <MenuItem value="client_meeting">Client Meeting</MenuItem>
                    <MenuItem value="court_appearance">Court Appearance</MenuItem>
                    <MenuItem value="document_preparation">Document Preparation</MenuItem>
                    <MenuItem value="consultation">Consultation</MenuItem>
                    <MenuItem value="administrative">Administrative</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={billable ? 'Billable' : 'Non-Billable'}
                    color={billable ? 'success' : 'default'}
                    size="small"
                    onClick={() => setBillable(!billable)}
                    clickable
                  />
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Save Time Entry Dialog */}
      <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Time Entry</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Duration: {formatTime(elapsedTime)}
          </Alert>
          
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the work performed"
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <CaseAutocomplete
              label="Case"
              value={selectedCase || null}
              onChange={(c) => {
                setSelectedCase(c?.id || '');
                const clientId = c?.clientId || c?.client?.id;
                if (clientId) setSelectedClient(clientId);
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <ClientAutocomplete
              label="Client"
              value={selectedClient || null}
              onChange={(c) => setSelectedClient(c?.id || '')}
            />
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Task Type</InputLabel>
            <Select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              label="Task Type"
            >
              <MenuItem value="legal_work">Legal Work</MenuItem>
              <MenuItem value="research">Research</MenuItem>
              <MenuItem value="client_meeting">Client Meeting</MenuItem>
              <MenuItem value="court_appearance">Court Appearance</MenuItem>
              <MenuItem value="document_preparation">Document Preparation</MenuItem>
              <MenuItem value="consultation">Consultation</MenuItem>
              <MenuItem value="administrative">Administrative</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={billable ? 'Billable' : 'Non-Billable'}
              color={billable ? 'success' : 'default'}
              onClick={() => setBillable(!billable)}
              clickable
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEntry}
            variant="contained"
            disabled={!description.trim() || saveTimeEntryMutation.isPending}
          >
            {saveTimeEntryMutation.isPending ? 'Saving...' : 'Save Entry'}
          </Button>
          <Button
            onClick={async () => {
              if (!description.trim()) {
                toast.error('Please enter a description');
                return;
              }
              
              setStartNewAfterSave(true);
              try {
                await handleSaveEntry();
              } catch (error) {
                console.error('Error in Save & New Timer:', error);
                setStartNewAfterSave(false);
              }
            }}
            variant="outlined"
            disabled={!description.trim() || saveTimeEntryMutation.isPending}
          >
            Save & New Timer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimerWidget; 